/**
 * ADR-048 tiered permission model — client-side projection (D8).
 *
 * The role's stored representation is a flat set of Django permission ids; the
 * tiered model (per-category Observe / Participate / Manage level, plus
 * cross-cutting capability toggles) is a *projection* over that set, computed
 * here at the UI boundary. Mirrors the server-side catalog in
 * `server/apps/accounts/permission_levels.py` (D5) but is built
 * entirely from the `GET /api/v2/permissions/` response (D7) — no
 * codenames are hardcoded, so it tracks whatever the backend catalog declares.
 *
 * Two invariants from the amendment (Decision 7) drive the design:
 *   1. Load infers each category's highest fully-satisfied tier; a set that
 *      falls *between* tiers surfaces as `custom` with its raw codenames.
 *   2. Save never silently drops permissions — codenames the catalog does not
 *      own (leftovers) and a `custom` category's raw set are both preserved.
 *
 * See `doc/adr/decisions/048-client-configurable-roles.md` (Decision 2 levels, Decision 7
 * Custom fallback) and `…-implementation.md` §2 (API contract) + §3 (projection).
 */

import type {
  CapabilityMeta,
  Permission,
  PermissionCategoryMeta,
  PermissionLevel,
  PermissionLevelOrCustom,
} from '../types/role';

/** Ordered, cumulative levels (ADR-048 Decision 2). `none` grants nothing. */
export const LEVELS: PermissionLevel[] = [
  'none',
  'observe',
  'participate',
  'manage',
];

/** Customer-facing labels for the level control (Decision 2 / gate Q1). */
export const LEVEL_LABELS: Record<PermissionLevelOrCustom, string> = {
  none: 'None',
  observe: 'Observe',
  participate: 'Participate',
  manage: 'Manage',
  custom: 'Custom',
};

/** The three audience bands, rendered as section headers in this order (Decision 1). */
export const BAND_ORDER = ['operational', 'configuration', 'platform'] as const;

/** Section-header labels for each band. */
export const BAND_LABELS: Record<string, string> = {
  operational: 'Operational',
  configuration: 'Configuration',
  platform: 'Platform & Integration',
};

/** Display label for a level, supporting optional i18n translation. */
export function getLevelLabel(
  level: PermissionLevelOrCustom,
  t?: (key: string, fallback: string) => string,
): string {
  if (t) {
    const keyMap: Record<PermissionLevelOrCustom, string> = {
      none: 'permissions.levels.none',
      observe: 'permissions.levels.observe',
      participate: 'permissions.levels.participate',
      manage: 'permissions.levels.manage',
      custom: 'permissions.levels.custom',
    };
    return t(keyMap[level], LEVEL_LABELS[level]);
  }
  return LEVEL_LABELS[level];
}

/** Display label for a band key, falling back to the raw key for forward-compat. */
export function bandLabel(
  band: string,
  t?: (key: string, fallback: string) => string,
): string {
  if (t) {
    const keyMap: Record<string, string> = {
      operational: 'permissions.bands.operational',
      configuration: 'permissions.bands.configuration',
      platform: 'permissions.bands.platform',
    };
    if (keyMap[band]) {
      return t(keyMap[band], BAND_LABELS[band]);
    }
  }
  return BAND_LABELS[band] ?? band;
}

function isLevel(value: string | null): value is PermissionLevel {
  return value !== null && (LEVELS as string[]).includes(value);
}

/**
 * Indexed, codename-keyed view of the permission catalog, derived once from the
 * endpoint response and reused for inference + expansion.
 */
export interface PermissionCatalog {
  categories: PermissionCategoryMeta[];
  capabilities: CapabilityMeta[];
  /** codename → permission id (only permissions present in the API results). */
  idByCodename: Map<string, number>;
  /** permission id → codename. */
  codenameById: Map<number, string>;
  /** category key → level → the *incremental* codenames introduced at that level. */
  incrementalByCategory: Map<string, Map<PermissionLevel, Set<string>>>;
  /** category key → applicable levels (mirrors the API; always begins with `none`). */
  applicableLevels: Map<string, PermissionLevel[]>;
  /** Every codename placed in any category tier. */
  categoryCodenames: Set<string>;
  /** Every cross-cutting capability codename. */
  capabilityCodenames: Set<string>;
}

/**
 * Build the indexed catalog from the three blocks of the permissions endpoint.
 * `permissions` must be the *full* (unpaginated) result set so the id ⇄ codename
 * map and the per-level codename sets are complete.
 */
export function buildPermissionCatalog(
  permissions: Permission[],
  categories: PermissionCategoryMeta[],
  capabilities: CapabilityMeta[],
): PermissionCatalog {
  const idByCodename = new Map<string, number>();
  const codenameById = new Map<number, string>();
  const incrementalByCategory = new Map<string, Map<PermissionLevel, Set<string>>>();
  const categoryCodenames = new Set<string>();

  for (const perm of permissions) {
    idByCodename.set(perm.codename, perm.id);
    codenameById.set(perm.id, perm.codename);

    // Tiered placement: a non-null category at a real (non-`none`) level.
    if (perm.category && isLevel(perm.level) && perm.level !== 'none') {
      categoryCodenames.add(perm.codename);
      let byLevel = incrementalByCategory.get(perm.category);
      if (!byLevel) {
        byLevel = new Map();
        incrementalByCategory.set(perm.category, byLevel);
      }
      let bucket = byLevel.get(perm.level);
      if (!bucket) {
        bucket = new Set();
        byLevel.set(perm.level, bucket);
      }
      bucket.add(perm.codename);
    }
  }

  const applicableLevels = new Map<string, PermissionLevel[]>();
  for (const category of categories) {
    applicableLevels.set(
      category.key,
      category.applicable_levels.filter(isLevel),
    );
  }

  return {
    categories,
    capabilities,
    idByCodename,
    codenameById,
    incrementalByCategory,
    applicableLevels,
    categoryCodenames,
    capabilityCodenames: new Set(capabilities.map((c) => c.codename)),
  };
}

/** Every codename placed in any tier of a category (union over all its levels). */
export function categoryCodenamesFor(
  catalog: PermissionCatalog,
  categoryKey: string,
): Set<string> {
  const result = new Set<string>();
  const byLevel = catalog.incrementalByCategory.get(categoryKey);
  if (byLevel) {
    for (const bucket of byLevel.values()) {
      for (const codename of bucket) result.add(codename);
    }
  }
  return result;
}

/**
 * Cumulative codename set granted by `level` in a category — the union of that
 * tier and every lower declared tier (Decision 2). `none` grants nothing.
 */
export function effectiveCodenames(
  catalog: PermissionCatalog,
  categoryKey: string,
  level: PermissionLevel,
): Set<string> {
  const result = new Set<string>();
  if (level === 'none') return result;
  const byLevel = catalog.incrementalByCategory.get(categoryKey);
  if (!byLevel) return result;
  const cutoff = LEVELS.indexOf(level);
  for (let i = 1; i <= cutoff; i += 1) {
    const bucket = byLevel.get(LEVELS[i]);
    if (bucket) for (const codename of bucket) result.add(codename);
  }
  return result;
}

export function isSubset(subset: Set<string>, superset: Set<string>): boolean {
  for (const value of subset) if (!superset.has(value)) return false;
  return true;
}

/**
 * The editable selection state behind the picker. `levelByCategory` is the live
 * per-category choice; the remaining fields are the load-time baseline that
 * makes save non-destructive (Decision 7):
 *   - `customCodenamesByCategory` — the raw codenames a `custom` category keeps.
 *   - `leftoverIds` — permission ids the catalog does not own, preserved verbatim.
 */
export interface RolePermissionSelection {
  levelByCategory: Record<string, PermissionLevelOrCustom>;
  capabilityIds: number[];
  leftoverIds: number[];
  customCodenamesByCategory: Record<string, string[]>;
  /** category key → whether the original set fell between tiers (offer `custom`). */
  customByCategory: Record<string, boolean>;
}

/**
 * Project a role's flat permission ids onto the tiered model (load).
 *
 * For each category the level is the highest tier fully contained in the role's
 * present set; if the present set has extras beyond that tier (but not enough to
 * reach the next), the category is `custom` and its raw codenames are retained.
 * Capabilities are read off the capability ids the role holds; everything the
 * catalog does not own becomes a preserved leftover.
 */
export function inferRoleSelection(
  selectedIds: number[],
  catalog: PermissionCatalog,
): RolePermissionSelection {
  const selectedIdSet = new Set(selectedIds);

  const presentCodenames = new Set<string>();
  for (const id of selectedIds) {
    const codename = catalog.codenameById.get(id);
    if (codename) presentCodenames.add(codename);
  }

  // Ids the picker owns: every tier codename + every capability codename that
  // resolves to a real permission id. Anything else the role holds is a leftover.
  const ownedIds = new Set<number>();
  for (const codename of catalog.categoryCodenames) {
    const id = catalog.idByCodename.get(codename);
    if (id !== undefined) ownedIds.add(id);
  }
  for (const codename of catalog.capabilityCodenames) {
    const id = catalog.idByCodename.get(codename);
    if (id !== undefined) ownedIds.add(id);
  }
  const leftoverIds = selectedIds.filter((id) => !ownedIds.has(id));

  const levelByCategory: Record<string, PermissionLevelOrCustom> = {};
  const customCodenamesByCategory: Record<string, string[]> = {};
  const customByCategory: Record<string, boolean> = {};

  for (const category of catalog.categories) {
    const applicable = catalog.applicableLevels.get(category.key) ?? [];
    // Candidate tiers, highest first.
    const candidates = applicable.filter((l) => l !== 'none').reverse();

    const all = categoryCodenamesFor(catalog, category.key);
    const present = new Set<string>();
    for (const codename of presentCodenames) {
      if (all.has(codename)) present.add(codename);
    }

    let level: PermissionLevel = 'none';
    for (const candidate of candidates) {
      if (isSubset(effectiveCodenames(catalog, category.key, candidate), present)) {
        level = candidate;
        break;
      }
    }

    const isCustom = !isSubset(
      present,
      effectiveCodenames(catalog, category.key, level),
    );
    levelByCategory[category.key] = isCustom ? 'custom' : level;
    customByCategory[category.key] = isCustom;
    customCodenamesByCategory[category.key] = [...present].sort();
  }

  const capabilityIds: number[] = [];
  for (const capability of catalog.capabilities) {
    const id = catalog.idByCodename.get(capability.codename);
    if (id !== undefined && selectedIdSet.has(id)) capabilityIds.push(id);
  }

  return {
    levelByCategory,
    capabilityIds,
    leftoverIds,
    customCodenamesByCategory,
    customByCategory,
  };
}

/**
 * Expand the tiered selection back to a flat set of permission ids (save).
 *
 * Each category contributes either its `custom` raw set or the cumulative set
 * for its chosen level; capability toggles and the preserved leftovers are
 * unioned in. Leftovers are never dropped (Decision 7).
 */
export function selectionToPermissionIds(
  selection: RolePermissionSelection,
  catalog: PermissionCatalog,
): number[] {
  const ids = new Set<number>(selection.leftoverIds);

  for (const category of catalog.categories) {
    const value = selection.levelByCategory[category.key] ?? 'none';
    const codenames =
      value === 'custom'
        ? selection.customCodenamesByCategory[category.key] ?? []
        : effectiveCodenames(catalog, category.key, value);
    for (const codename of codenames) {
      const id = catalog.idByCodename.get(codename);
      if (id !== undefined) ids.add(id);
    }
  }

  for (const id of selection.capabilityIds) ids.add(id);

  return [...ids];
}

/** Count of permissions a category currently grants, for the per-category summary. */
export function selectedCountForCategory(
  selection: RolePermissionSelection,
  catalog: PermissionCatalog,
  categoryKey: string,
): number {
  const value = selection.levelByCategory[categoryKey] ?? 'none';
  if (value === 'custom') {
    return (selection.customCodenamesByCategory[categoryKey] ?? []).length;
  }
  return effectiveCodenames(catalog, categoryKey, value).size;
}
